import InfoIcon from "@mui/icons-material/Info";
import { Box, Divider, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { controlKeys } from "../../../../api/queryKeys";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import ResizableDrawer from "../../../../components/ResizableDrawer";
import { DRAWER_TABS } from "../../../../utils/constants";
import ControlRemediationDrawer from "../../controls/components/ControlRemediationDrawer/ControlRemediationDrawer";
import NISTCategoryIdentifier from "../../controls/components/NISTCategoryIdentifier";
import NISTLabel from "../../controls/components/NISTLabel";
import PercentageLabel from "../../frameworks/components/PercentageLabel";
import TabbedMitigationControlsTable from "../../steps/components/TabbedMitigationControlsTable";
import { nistDescription } from "../nist_description";

const CategoryDrawer = ({ open, onClose, category, nistFunction }) => {
  const { fetchAllControls, trackEvent } = useAPI();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedControl, setSelectedControl] = useState(null);

  const colors = useColors();
  const { data: controls, isLoading: controlsLoading } = useQuery({
    queryKey: controlKeys.all,
    queryFn: async () => {
      const controls = await fetchAllControls();
      return controls;
    },
    select: (response) => response.data,
    enabled: category?.controls?.length > 0,
  });

  const categoryControls = useMemo(() => {
    return controls?.filter((control) =>
      category?.controls.includes(control.control_id)
    );
  }, [controls, category]);

  const onRowClick = (control_id) => {
    setOpenDrawer(true);
    setSelectedControl(
      categoryControls?.find((control) => control.control_id === control_id)
    );

    trackEvent(
      "User clicked on a control " +
        control_id +
        " in the category drawer: " +
        category?.name
    );
  };

  const resetSelection = () => {
    setOpenDrawer(false);
    setSelectedControl(null);
  };

  return (
    <ResizableDrawer
      initialWidth={950}
      minWidth={950}
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: "8px 0 0 0",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: 3,
          gap: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
              }}
            >
              <NISTLabel nistFunction={nistFunction.function} size="medium" />
              <NISTCategoryIdentifier
                nistFunction={nistFunction.function}
                nistIdentifiers={[category?.id]}
                size="medium"
              />
            </Box>
            <PercentageLabel
              applicable={category.score >= 0}
              percentage={category.score >= 0 ? category.score : 0}
              sx={{ borderRadius: "10px", height: "24px" }}
            />
          </Box>
          <Typography variant="h5">{category.name.split("(")[0]}</Typography>
          <Typography variant="body1">
            {nistDescription[category.id]}
          </Typography>
        </Box>
        <Divider />
        {/* Controls */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1">Related Controls</Typography>
            <CustomTooltip
              title="Displays controls mapped to this category by status. Snoozed and Excluded are counted as Compliant in the overall category score. Click a row to view control details."
              placement="top"
            >
              <InfoIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color: colors.tooltip_info_icon,
                }}
              />
            </CustomTooltip>
          </Box>
          {!controlsLoading ? (
            categoryControls?.length > 0 ? (
              <>
                {/* Table of associated controls */}
                <TabbedMitigationControlsTable
                  mitigatingControls={categoryControls}
                  loading={controlsLoading}
                  onRowClick={onRowClick}
                  boxSXProps={{
                    border: "0px",
                  }}
                  filterColumns={[
                    "is_detected",
                    "control_id",
                    "name_for_gap_state",
                    "name_for_correct_state",
                    "tool",
                    "remediation_priority",
                    "affected_entity_counts_by_type",
                    "severity",
                  ]}
                />

                {/* Drawer to show selected control details */}
                <ControlRemediationDrawer
                  open={openDrawer}
                  onClose={resetSelection}
                  selectedTab={DRAWER_TABS.CONTROL_TAB}
                  selectedControl={selectedControl}
                />
              </>
            ) : (
              <Typography variant="subtitle2">
                No controls found for this category
              </Typography>
            )
          ) : (
            <LoadingIcon size={25} />
          )}
        </Box>
      </Box>
    </ResizableDrawer>
  );
};

export default CategoryDrawer;
